import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  addDoc,
  updateDoc,
  deleteDoc,
  DocumentData,
  DocumentSnapshot,
  Query,
  startAfter,
} from 'firebase/firestore';
import { firebaseStorage } from '@/src/auth/FirebaseAuth';

const db = firebaseStorage;

export const FirebaseDataProvider = {
  getList: async (resource, { pagination, sort, filter, meta }) => {
    const { page, perPage } = pagination;
    const { field = 'username', order = 'ASC' } = sort;
    let ref: Query<DocumentData> = collection(db, resource);

    const constraints = [
      field ? orderBy(field, order.toLowerCase()) : null,
      meta.lastRef ? startAfter(meta.lastRef?.[field]) : null,
      perPage ? limit(perPage) : null,
    ].filter((val) => !!val);

    if (filter) {
      Object.keys(filter).forEach((key) => {
        ref = query(ref, where(key, '==', filter[key]), ...constraints);
      });
    }

    ref = query(ref, ...constraints);
    const snapshot = await getDocs(ref);

    const result = {
      data: snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
      total: snapshot.size * page + 1,
    };
    return result;
  },

  getOne: async (resource, { id }) => {
    const docRef = doc(db, resource, id);
    let docSnap: DocumentSnapshot<DocumentData>;
    try {
      docSnap = await getDoc(docRef);
    } catch (e) {
      console.log('doesnt exist', e);
    }

    if (!docSnap.exists()) {
      throw new Error('Document not found');
    }
    return { data: { id: docSnap.id, ...docSnap.data() } };
  },

  getMany: async (resource, { ids }) => {
    const data = await Promise.all(
      ids.map((id) => FirebaseDataProvider.getOne(resource, { id }))
    );
    return { data: data.map((d) => d.data) };
  },

  getManyReference: async (resource, { target, id }) => {
    const ref = query(collection(db, resource), where(target, '==', id));
    const snapshot = await getDocs(ref);

    return {
      data: snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
      total: snapshot.size,
    };
  },

  create: async (resource, { data }) => {
    const docRef = await addDoc(collection(db, resource), data);
    return { data: { id: docRef.id, ...data } };
  },

  update: async (resource, { id, data }) => {
    const docRef = doc(db, resource, id);
    await updateDoc(docRef, data);
    return { data: { id, ...data } };
  },

  updateMany: async (resource, { ids, data }) => {
    await Promise.all(
      ids.map((id) => FirebaseDataProvider.update(resource, { id, data }))
    );
    return { data: ids };
  },

  delete: async (resource, { id }) => {
    const docRef = doc(db, resource, id);
    await deleteDoc(docRef);
    return { data: { id } };
  },

  deleteMany: async (resource, { ids }) => {
    await Promise.all(ids.map((id) => FirebaseDataProvider.delete(resource, { id })));
    return { data: ids };
  },

  getUserByUid: async (uid: string) => {
    const usersSnap = await getDocs(
      query(collection(firebaseStorage, 'users'), where('uid', '==', uid))
    );
    if (usersSnap.empty) {
      return null;
    }

    const userDoc = usersSnap.docs[0];
    return { id: userDoc.id, ...userDoc.data() };
  },
};
